import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import Quill from 'quill';
import {
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    Box,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Tooltip,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    getToDoPrioritys,
    getToDoStatus,
} from 'core/API/todo';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { NavLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import styles from './AddBroadcast.module.scss';
import { getSingleBroadcastMessage, PostBroadcast, postBroadcastThread, getWorkplaceEmployeesUsers, uploadBroadcastFile, BroadcastFile, deleteBroadcastAttachment } from 'core/API/broadcast';
import { BroadcastReplyLocationState, buildThreadReplyPayload, formatReplyTitle, isReplyToCurrentUser, resolveReplyTarget } from './utils/broadcastReply';
import { apiService } from 'core/services/apiService';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { NO_WORKPLACE_MESSAGE, getCurrentWorkplaceId } from 'core/session/workplaceStore';

// Add RouteParams type for useParams
type RouteParams = { id?: string };

const AddBroadcast: React.FC = () => {
    const { showToast } = useToastContext();
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation<BroadcastReplyLocationState>();
    const workplace = useWorkplaceUser();
    const { id } = useParams<RouteParams>();
    const replyState = !id && location.state?.replyToId ? location.state : undefined;
    const isReply = Boolean(replyState?.replyToId && replyState.replyMode);
    const replyApplied = useRef(false);
    const [replyTargetLabel, setReplyTargetLabel] = useState('');
    const [replyQuillKey, setReplyQuillKey] = useState('new-broadcast');

    // Broadcast permission checks (module-level for create/view; label-based for restricted groups dropdown)
    const {
        hasModuleViewPermission,
        hasModuleEditPermission,
        hasViewPermission,
        hasEditPermission,
    } = usePermissionCheck();
    const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
    const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
    const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
    const canEditBroadcastMy = hasModuleEditPermission('Broadcast - My');
    const canEditBroadcastAll = hasModuleEditPermission('Broadcast - All');
    const canEditBroadcastOrganization = hasModuleEditPermission('Broadcast - Organization');
    // For dropdown: only EDIT permission controls access to special groups
    const hasBroadcastOrgPermission = hasEditPermission('Broadcast - Organization');
    const hasBroadcastAllPermission = hasEditPermission('Broadcast - All');

    // Check if user can create/edit broadcasts (edit permission controls add/send)
    const canCreateBroadcast = canEditBroadcastMy || canEditBroadcastAll || canEditBroadcastOrganization;
    const canViewBroadcast = canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization;

    // const userId = workplace?.data.user.id;
    const workplaceId = getCurrentWorkplaceId();

    const [form, setForm] = useState({
        title: "",
        from: "",
        to: "",
        noteMessage: '',
        priorityId: 2, // Default to Medium priority (ID 2)
    });

    const [isFormDirty, setIsFormDirty] = useState(false);
    const [openBackDialog, setOpenBackDialog] = useState(false);
    const [receiverType, setReceiverType] = useState<'User' | 'Broadcast Group'>('User');
    const [allowReplyAll, setAllowReplyAll] = useState(true);
    const [selectedValue, setSelectedValue] = useState<any>('');
    const [currentUserFullName, setCurrentUserFullName] = useState<string>('');
    const [errors, setErrors] = useState<{
        title?: string;
        assigned?: string;
        priority?: string;
        content?: string;
    }>({});
    const [value, setValue] = useState("");
    const [openUrlDialog, setOpenUrlDialog] = useState(false);
    const [urlInput, setUrlInput] = useState("");
    const [urlDisplayText, setUrlDisplayText] = useState("");
    const [attachments, setAttachments] = useState<BroadcastFile[]>([]);
    const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null);


    // Fetch current user data to get full name
    const currentUserId = Number(localStorage.getItem('userId') || '0');
    const { data: currentUserData } = useQuery(
        ['current-user', currentUserId],
        async () => {
            if (!currentUserId) return null;
            const response = apiService.responseHandler(await apiService.get(`/users/${currentUserId}`));
            return response;
        },
        {
            enabled: !!currentUserId,
            onSuccess: (user) => {
                if (user) {
                    const fullName = `${user.firstName || ''} ${user.middleName || ''} ${user.lastName || ''}`.trim().replace(/\s+/g, ' ');
                    setCurrentUserFullName(fullName);
                    setForm((prev) => ({ ...prev, from: fullName }));
                }
            }
        }
    );

    // Fetch broadcast groups
    const { data: broadcastGroupsData, isLoading: isBroadcastGroupsLoading } = useQuery(
        ['broadcast-groups', workplaceId],
        async () => {
            const response = apiService.responseHandler(await apiService.get('/broadcast_groups', {
                params: {
                    workplace: workplaceId || 1,
                    page: 1,
                    itemsPerPage: 100,
                    pagination: false,
                },
            }));
            if (response?.member && Array.isArray(response.member)) {
                return { member: response.member };
            }
            if (Array.isArray(response)) {
                return { member: response };
            }
            return { member: [] };
        },
        {
            enabled: !!workplaceId,
        }
    );

    const broadcastGroupsRaw = broadcastGroupsData?.member || [];

    // Show "Entire Organization" only with Broadcast - Organization; "Entire Workplace" only with Broadcast - All (label-based permission check)
    const broadcastGroups = useMemo(() => {
        return broadcastGroupsRaw.filter((group: any) => {
            const name = String(group.groupName || '').toLowerCase().trim();
            const isOrg = name.includes('entire organization') || group.organization_broadcast === true;
            const isWorkplace = name.includes('entire workplace') || (group.workplace_broadcast === true && !group.organization_broadcast);
            if (isOrg) return hasBroadcastOrgPermission;
            if (isWorkplace) return hasBroadcastAllPermission;
            return true; // other groups always show
        });
    }, [broadcastGroupsRaw, hasBroadcastOrgPermission, hasBroadcastAllPermission]);

    // Fetch existing broadcast data
        const { data: existingBroadcast, isLoading: isLoadingBroadcast, error: broadcastError } = useQuery(
            ['broadcast', id],
            () => getSingleBroadcastMessage(Number(id)),
            {
                enabled: !!id,
                onSuccess: (broadcast) => {
                    // Determine receiver type
                    const hasReceiverUser = !!broadcast.receiverUser;
                    const hasReceiverGroup = !!broadcast.receiverGroup;
                    const receiverTypeValue = hasReceiverUser ? 'User' : 'Broadcast Group';
                    
                    setReceiverType(receiverTypeValue);
                    
                    // Pre-fill form with existing data
                    setForm({
                        title: broadcast.title || '',
                        from: broadcast.sender ? `${broadcast.sender.firstName} ${broadcast.sender.lastName}` : '',
                        to: '',
                        noteMessage: '',
                        priorityId: broadcast.priority?.id || (typeof broadcast.priority === 'string' ? parseInt(broadcast.priority.split('/').pop()) : 1)
                    });
                    setValue(broadcast.content);

                    if (broadcast.attachments && Array.isArray(broadcast.attachments)) {
                        const normalizedAttachments = broadcast.attachments.map((att: any, index: number) => {
                            if (typeof att === 'string') {
                                return {
                                    id: index,
                                    '@id': att,
                                    name: `Attachment ${index + 1}`,
                                } as BroadcastFile;
                            }
                            return att as BroadcastFile;
                        });
                        setAttachments(normalizedAttachments);
                    }
                    
                    if (hasReceiverUser) {
                        setSelectedValue(broadcast.receiverUser.id);
                    } else if (hasReceiverGroup) {
                        const groupId = typeof broadcast.receiverGroup === 'string' 
                            ? parseInt(broadcast.receiverGroup.split('/').pop() || '0')
                            : broadcast.receiverGroup?.id || 0;
                        setSelectedValue(groupId);
                    }
                }
            }
        );

    const applyReplyPrefill = (broadcast: any) => {
        if (replyApplied.current || !replyState) return;
        const fetchedAllowsReplyAll = replyState.replySource ? true : broadcast?.isAllowReplyAll === true;
        if (replyState.replyMode === 'all' && (replyState.allowReplyAll !== true || !fetchedAllowsReplyAll)) {
            replyApplied.current = true;
            showToast('error', 'Reply to all is not allowed for this broadcast.');
            history.goBack();
            return;
        }
        if (isReplyToCurrentUser(broadcast, replyState.replyMode, currentUserId || null)) {
            replyApplied.current = true;
            showToast('error', 'You cannot send a message to yourself.');
            history.goBack();
            return;
        }
        const target = resolveReplyTarget(broadcast, replyState.replyMode, currentUserId || null);
        if (!target) {
            showToast('error', 'Could not determine who to reply to.');
            return;
        }
        replyApplied.current = true;
        setReceiverType(target.receiverType);
        setSelectedValue(target.id);
        setReplyTargetLabel(target.label);
        const priorityId = broadcast.priority?.id
            || (typeof broadcast.priority === 'string' ? parseInt(broadcast.priority.split('/').pop() || '', 10) : 0);
        setForm((prev) => ({
            ...prev,
            title: formatReplyTitle(broadcast.title),
            priorityId: priorityId || prev.priorityId,
        }));
        setValue('');
        setReplyQuillKey(`reply-${replyState.replyToId}-${replyState.parentThreadMessageId || 'root'}`);
    };

    useEffect(() => {
        if (isReply && replyState?.replySource) {
            applyReplyPrefill(replyState.replySource);
        }
    }, [isReply, replyState?.replySource, currentUserId]);

    useQuery(
        ['broadcast-reply-parent', replyState?.replyToId],
        () => getSingleBroadcastMessage(Number(replyState?.replyToId)),
        {
            enabled: isReply && !replyState?.replySource,
            onSuccess: (broadcast) => {
                applyReplyPrefill(broadcast);
            },
        }
    );

    const { mutate: postOrUpdateBroadcast, isLoading } = useMutation(
        (data: { kind: 'create'; body: any } | { kind: 'reply'; threadRootId: number; body: Record<string, unknown> }) =>
            data.kind === 'reply'
                ? postBroadcastThread(data.threadRootId, data.body)
                : PostBroadcast(data.body),
        {
            onSuccess: () => {
                showToast('success', isReply ? 'Reply sent successfully!' : 'Broadcast created successfully!');

                // Only reset form if it's a new broadcast (not editing)
                if (!id) {
                    setForm({
                        title: '',
                        from: currentUserFullName,
                        to: '',
                        noteMessage: '',
                        priorityId: 2, // Default to Medium priority (ID 2)
                    });
                    setSelectedValue('');
                    setReceiverType('User');
                }

                resetFormDirty();
                try {
                    const workplaceIdLS = getCurrentWorkplaceId();
                    queryClient.invalidateQueries(['broadcast', workplaceIdLS]);
                    queryClient.invalidateQueries(['broadcast-outbox', workplaceIdLS]);
                    queryClient.invalidateQueries(['broadcast-thread-inbox', workplaceIdLS]);
                    queryClient.invalidateQueries(['broadcast-thread-outbox', workplaceIdLS]);
                    if (replyState?.threadRootId) {
                        queryClient.invalidateQueries(['broadcast-thread', replyState.threadRootId]);
                    }
                } catch (e) {
                    console.warn('Failed to invalidate broadcast cache after add', e);
                }
                if (replyState?.threadRootId) {
                    history.push(ROUTES.VIEW_BROADCAST.replace(':id', String(replyState.threadRootId)));
                } else {
                    history.push(ROUTES.BROADCAST);
                }
            },
            onError: (error) => {
                console.error('Broadcast operation failed:', error);
                showToast('error', 'Failed to create Broadcast');
            },
        }
    );

    const { data: prioritysList, isLoading: isPrioritiesLoading } = useQuery(
        ['todo-priorities', workplaceId],
        () => getToDoPrioritys(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: statusList, isLoading: isStatusesLoading } = useQuery(
        ['todo-statuses', workplaceId],
        () => getToDoStatus(),
        {
            enabled: true,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const { data: usersList, isLoading: isUsersLoading } = useQuery(
        ['workplace_employees/users', workplaceId],
        () => getWorkplaceEmployeesUsers(workplaceId),
        {
            enabled: !!workplaceId,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    // Fetch groups from API

    // Normalize API responses to support different shapes
    const normalizedPriorities = Array.isArray(prioritysList?.member)
        ? prioritysList.member
        : Array.isArray(prioritysList?.['hydra:member'])
            ? prioritysList['hydra:member']
            : Array.isArray(prioritysList?.items)
                ? prioritysList.items
                : Array.isArray(prioritysList?.data)
                    ? prioritysList.data
                    : Array.isArray(prioritysList)
                        ? prioritysList
                        : [];

    const normalizedStatuses = Array.isArray(statusList?.member)
        ? statusList.member
        : Array.isArray(statusList?.['hydra:member'])
            ? statusList['hydra:member']
            : Array.isArray(statusList?.items)
                ? statusList.items
                : Array.isArray(statusList?.data)
                    ? statusList.data
                    : Array.isArray(statusList)
                        ? statusList
                        : [];

    const noPriorities = !isPrioritiesLoading && normalizedPriorities.length === 0;
    const noStatuses = !isStatusesLoading && normalizedStatuses.length === 0;

    // Normalize users response to array of users (support array or { member: [] } shapes)
    const normalizedUsers = Array.isArray(usersList)
        ? usersList
        : Array.isArray((usersList as any)?.member)
            ? (usersList as any).member
            : Array.isArray((usersList as any)?.['hydra:member'])
                ? (usersList as any)['hydra:member']
                : Array.isArray((usersList as any)?.data)
                    ? (usersList as any).data
                    : [];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setIsFormDirty(true);
        setForm((prev) => ({ ...prev, [name]: value }));

        // Clear per-field errors on change
        if (name === 'description') {
            setErrors((prev) => ({ ...prev, description: '' }));
        }
        if (name === 'dueDate') {
            setErrors((prev) => ({ ...prev, dueDate: '' }));
        }
        if (name === 'noteMessage') {
            setErrors((prev) => ({ ...prev, noteMessage: '' }));
        }
    };

    // Reset form dirty state when form is successfully submitted
    const resetFormDirty = () => {
        setIsFormDirty(false);
    };

    const handleReceiverTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReceiverType(e.target.value as 'User' | 'Broadcast Group');
        setIsFormDirty(true);
        setSelectedValue('');
        setErrors((prev) => ({ ...prev, assigned: '' }));
    };

    const handleBackClick = () => {
        // Don't show unsaved changes dialog in readonly/view mode
        if (id) {
            history.push(ROUTES.BROADCAST);
            return;
        }
        
        if (isFormDirty) {
            setOpenBackDialog(true);
        } else {
            history.push(ROUTES.BROADCAST);
        }
    };

    const handleDialogClose = (save: boolean) => {
        setOpenBackDialog(false);
        if (save) {
            handleSubmit({ preventDefault: () => {} } as React.FormEvent);
        } else {
            history.push(ROUTES.BROADCAST);
        }
    };

    // Enhanced validation for edit mode
    const validateForm = () => {
        const nextErrors: {
            title?: string;
            assigned?: string;
            priority?: string;
            content?: string;
        } = {};
        
        // Validate title
        if (!form.title.trim()) {
            nextErrors.title = 'Title is required';
        } else if (form.title.length > 250) {
            nextErrors.title = 'Title must be ≤ 250 characters';
        }
        
        // Validate receiver (To field)
        if (!selectedValue) {
            nextErrors.assigned = `Please select a ${receiverType === 'User' ? 'user' : 'broadcast group'}`;
        }
        
        // Validate priority
        if (!form.priorityId || form.priorityId === 0) {
            nextErrors.priority = 'Priority is required';
        }
        
        // Validate content (ReactQuill editor)
        // Rule: Content is required ONLY when there are no image attachments.
        // If at least one image is attached, a blank message is allowed.
        if (!attachments || attachments.length === 0) {
            // Check if there's text content, images, or links in the editor itself
            const contentText = value.replace(/<[^>]*>/g, '').trim(); // Strip HTML tags
            const hasImages = /<img[^>]*>/i.test(value); // Check for image tags
            const hasLinks = /<a[^>]*href[^>]*>/i.test(value); // Check for link tags
            
            if (!contentText && !hasImages && !hasLinks) {
                nextErrors.content = 'Content is required';
            }
        }
        
        setErrors(nextErrors);
        return nextErrors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isUploadingImages) {
            showToast('error', 'Please wait for images to finish uploading before sending.');
            return;
        }

        if (receiverType === 'User' && currentUserId && Number(selectedValue) === currentUserId) {
            showToast('error', 'You cannot send a message to yourself.');
            return;
        }

        const nextErrors = validateForm();
        if (Object.keys(nextErrors).length > 0) {
            if (nextErrors.content) {
                showToast('error', nextErrors.content);
            }
            return;
        }

        const workplaceIdLS = getCurrentWorkplaceId();
        if (!workplaceIdLS) {
            showToast('error', NO_WORKPLACE_MESSAGE);
            return;
        }

        const attachmentIris = attachments.map((a) =>
            (a as any)['@id'] ?? `/api/broadcast_attachments/${a.id}`
        );

        const body: any = {
            receiverUser: receiverType === 'User' ? `/api/users/${selectedValue}` : null,
            receiverGroup: receiverType === 'Broadcast Group' ? `/api/broadcast_groups/${selectedValue}` : null,
            workplace: `/api/workplaces/${workplaceIdLS}`,
            title: form.title,
            content: value,
            priority: `/api/priorities/${form.priorityId}`,
            attachments: attachmentIris,
            isAllowReplyAll: allowReplyAll,
        };

        try {
            if (isReply && replyState) {
                if (replyState.replyMode === 'all' && replyState.allowReplyAll !== true) {
                    showToast('error', 'Reply to all is not allowed for this broadcast.');
                    return;
                }
                postOrUpdateBroadcast({
                    kind: 'reply',
                    threadRootId: replyState.threadRootId,
                    body: buildThreadReplyPayload({
                        parentThreadMessageId: replyState.parentThreadMessageId ?? null,
                        replyType: replyState.replyMode,
                        receiverType,
                        selectedValue,
                        title: form.title,
                        content: value,
                        priorityId: form.priorityId,
                    }),
                });
            } else {
                postOrUpdateBroadcast({ kind: 'create', body });
            }
        } catch (error) {
            console.error('Error submitting broadcast:', error);
            showToast('error', 'Failed to submit broadcast. Please try again.');
        }
    };

    const handleLinkClick = useCallback(() => {
        setUrlInput("");
        setUrlDisplayText("");
        setOpenUrlDialog(true);
    }, []);

    const handleUrlDialogSubmit = () => {
        if (urlInput.trim() && quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            const displayText = urlDisplayText.trim() || urlInput.trim();
            
            if (range) {
                // If there's selected text, format it as a link
                if (range.length > 0) {
                    quill.formatText(range.index, range.length, 'link', urlInput.trim());
                } else {
                    // Otherwise, insert new text with link format
                    quill.insertText(range.index, displayText, 'link', urlInput.trim());
                    quill.setSelection(range.index + displayText.length);
                }
            }
        }
        setOpenUrlDialog(false);
        setUrlInput("");
        setUrlDisplayText("");
    };

    const quillRef = useRef<ReactQuill>(null);
    const contentEditorRef = useRef<HTMLDivElement | null>(null);
    const imageInputRef = useRef<HTMLInputElement | null>(null);

    const MAX_IMAGES = 5;
    const MAX_IMAGE_SIZE_MB = 5;

    const modules = useMemo(() => {
        const toolbarOptions = [
            [{ header: [1, 2, false] }],
            ["bold", "italic", "underline"],
            ["link"],
        ];
        
        return {
            toolbar: {
                container: toolbarOptions,
                handlers: {
                    link: handleLinkClick,
                },
            },
            clipboard: {
                matchVisual: false,
                // Prevent images from being pasted or dropped into the editor
                matchers: [
                    [
                        'img',
                        // Quill 2 composes matcher results, so return a real (empty) Delta.
                        () => new (Quill.import('delta') as any)(),
                    ],
                ],
            }
        };
    }, [handleLinkClick]);

    // Add tooltip to URL button
    useEffect(() => {
        const addTooltips = () => {
            const linkButton = document.querySelector('.ql-link') as HTMLElement;
            
            if (linkButton && !linkButton.getAttribute('title')) {
                linkButton.setAttribute('title', 'Add URL');
            }
        };

        // Add tooltips after a short delay to ensure toolbar is rendered
        const timer = setTimeout(addTooltips, 100);
        
        // Also try to add tooltips when the component updates
        const observer = new MutationObserver(addTooltips);
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
            observer.observe(toolbar, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [value]);

    const handleOpenImagePicker = () => {
        if (imageInputRef.current && !id) {
            imageInputRef.current.click();
        }
    };

    const handleRemoveAttachment = async (index: number) => {
        const attachment = attachments[index];

        try {
            if (attachment?.id) {
                await deleteBroadcastAttachment(attachment.id);
            }
        } catch (error) {
            console.error('Failed to delete attachment:', error);
            setAttachmentsError('Failed to delete image. Please try again.');
            return;
        }

        setAttachments((prev) => {
            const next = [...prev];
            const [removed] = next.splice(index, 1);

            if (removed && removed.previewUrl) {
                URL.revokeObjectURL(removed.previewUrl);
            }

            return next;
        });
    };

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        if (isUploadingImages) {
            return;
        }

        const errors: string[] = [];

        const remainingSlots = MAX_IMAGES - attachments.length;
        if (remainingSlots <= 0) {
            setAttachmentsError(`You can attach up to ${MAX_IMAGES} images.`);
            event.target.value = '';
            return;
        }

        const selectedFiles = Array.from(files).slice(0, remainingSlots);
        const validFiles: File[] = [];

        selectedFiles.forEach((file) => {
            if (!file.type.startsWith('image/')) {
                errors.push('Only image files are allowed.');
                return;
            }

            if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                const msg = `${file.name} is larger than ${MAX_IMAGE_SIZE_MB}MB.`;
                errors.push(msg);
                showToast('error', msg);
                return;
            }

            validFiles.push(file);
        });

        if (validFiles.length === 0) {
            setAttachmentsError(errors.join(' '));
            event.target.value = '';
            return;
        }

        try {
            setIsUploadingImages(true);
            const uploaded = await Promise.all(
                validFiles.map(async (file) => {
                    const apiFile = await uploadBroadcastFile(file);
                    const previewUrl = URL.createObjectURL(file);

                    return {
                        ...apiFile,
                        name: (apiFile as any).fileName || apiFile.name || file.name,
                        previewUrl,
                    } as BroadcastFile;
                })
            );

            setAttachments((prev) => [...prev, ...uploaded]);
            setAttachmentsError(errors.length ? errors.join(' ') : null);
        } catch (error) {
            console.error('Image upload failed:', error);
            setAttachmentsError('Failed to upload images. Please try again.');
        } finally {
            setIsUploadingImages(false);
            event.target.value = '';
        }
    };

    useEffect(() => {
        const editor = document.querySelector('.ql-editor') as HTMLDivElement | null;
        if (!editor) {
            return;
        }

        const handlePaste = async (event: ClipboardEvent) => {
            if (id || !event.clipboardData) {
                return;
            }

            const items = Array.from(event.clipboardData.items || []);
            const imageFiles: File[] = [];

            items.forEach((item) => {
                if (item.kind === 'file' && item.type.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) {
                        imageFiles.push(file);
                    }
                }
            });

            if (imageFiles.length === 0) {
                return;
            }

            // Prevent Quill from inserting the image into the editor
            event.preventDefault();

            if (isUploadingImages) {
                return;
            }

            const remainingSlots = MAX_IMAGES - attachments.length;
            if (remainingSlots <= 0) {
                setAttachmentsError(`You can attach up to ${MAX_IMAGES} images.`);
                return;
            }

            const selectedFiles = imageFiles.slice(0, remainingSlots);
            const errors: string[] = [];
            const validFiles: File[] = [];

            selectedFiles.forEach((file) => {
                if (!file.type.startsWith('image/')) {
                    errors.push('Only image files are allowed.');
                    return;
                }

                if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
                    const msg = `${file.name} is larger than ${MAX_IMAGE_SIZE_MB}MB.`;
                    errors.push(msg);
                    showToast('error', msg);
                    return;
                }

                validFiles.push(file);
            });

            if (validFiles.length === 0) {
                setAttachmentsError(errors.join(' '));
                return;
            }

            try {
                setIsUploadingImages(true);
                const uploaded = await Promise.all(
                    validFiles.map(async (file) => {
                        const apiFile = await uploadBroadcastFile(file);
                        const previewUrl = URL.createObjectURL(file);

                        return {
                            ...apiFile,
                            name: (apiFile as any).fileName || apiFile.name || file.name,
                            previewUrl,
                        } as BroadcastFile;
                    })
                );

                setAttachments((prev) => [...prev, ...uploaded]);
                setAttachmentsError(errors.length ? errors.join(' ') : null);
            } catch (error) {
                console.error('Image upload failed:', error);
                setAttachmentsError('Failed to upload images. Please try again.');
            } finally {
                setIsUploadingImages(false);
            }
        };

        editor.addEventListener('paste', handlePaste);

        return () => {
            editor.removeEventListener('paste', handlePaste);
        };
    }, [id, attachments.length, isUploadingImages]);

    useEffect(() => {
        if (!errors.content || !contentEditorRef.current) {
            return;
        }
        contentEditorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [errors.content]);

    // If user doesn't have permission to view or create broadcasts, show error message
    if (!canViewBroadcast && !canCreateBroadcast) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={() => history.push(ROUTES.BROADCAST)}
                    >
                        Back
                    </Button>
                </Box>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        You do not have permissions to view or create broadcasts.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={1}
            style={{
                padding: '24px',
                paddingBottom: '24px',
                maxWidth: '100%',
                margin: '0 auto',
                height: 'auto',
                overflow: 'visible',
                boxSizing: 'border-box',
            }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                    onClick={handleBackClick}
                >
                    Back
                </Button>
                {!id && canCreateBroadcast ? (<Button
                    type="submit"
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
                    disabled={isLoading}
                    onClick={handleSubmit}
                >
                    {isLoading ? 'Sending...' : 'Send'}
                </Button>) : ""}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                {id ? 'View Broadcast' : 'Send Message'}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="from"
                            label="From"
                            fullWidth
                            required
                            value={form.from}
                            InputProps={{
                                readOnly: true,
                            }}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="priorityId"
                            select
                            label="Priority"
                            fullWidth
                            required
                            value={form.priorityId}
                            onChange={(e) => {
                                const value = Number((e.target as HTMLInputElement).value);
                                setForm((prev) => ({ ...prev, priorityId: value }));
                                setIsFormDirty(true);
                                setErrors((prev) => ({ ...prev, priority: '' }));
                            }}
                            disabled={!!id}
                            error={!!errors.priority}
                            helperText={errors.priority}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
                                    disableScrollLock: true,
                                },
                            }}
                        >
                            {isPrioritiesLoading ? (
                                <MenuItem disabled value="">
                                    Loading priorities...
                                </MenuItem>
                            ) : noPriorities ? (
                                <MenuItem disabled value="">
                                    No priorities found
                                </MenuItem>
                            ) : (
                                normalizedPriorities.map((priority: any) => (
                                    <MenuItem value={priority.id} key={priority.id}>
                                        {priority.label ?? priority.name}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>
                    </Grid>

                    {!id && (
                        <Grid item xs={12} sx={{ pb: 0 }}>
                            <FormControl component="fieldset" sx={{ mb: 0 }}>
                                <RadioGroup
                                    row
                                    value={receiverType}
                                    onChange={handleReceiverTypeChange}
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        '& .MuiFormControlLabel-root': { mr: 1, my: 0 },
                                        '& .MuiRadio-root': {
                                            padding: '4px',
                                            color: 'rgb(68, 99, 136)',
                                            '&.Mui-checked': {
                                                color: 'rgb(68, 99, 136)',
                                            },
                                        },
                                    }}
                                >
                                    <FormControlLabel 
                                        value="User" 
                                        control={<Radio />} 
                                        label="User"
                                        disabled={isReply}
                                    />
                                    <FormControlLabel 
                                        value="Broadcast Group" 
                                        control={<Radio />} 
                                        label="Broadcast Group"
                                        disabled={isReply}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                select
                                size="small"
                                label="To"
                                value={selectedValue || ''}
                                onChange={(e) => {
                                    const value = Number((e.target as HTMLInputElement).value);
                                    setSelectedValue(value);
                                    setIsFormDirty(true);
                                    setErrors((prev) => ({ ...prev, assigned: '' }));
                                }}
                                fullWidth
                                required
                                disabled={!!id || isReply}
                                error={!!errors.assigned}
                                helperText={isReply ? 'Recipient is locked for this reply.' : errors.assigned || undefined}
                            >
                                {!selectedValue && (
                                    <MenuItem value="" disabled>
                                        Select {receiverType === 'User' ? 'User' : 'Broadcast Group'}
                                    </MenuItem>
                                )}
                                {isReply && selectedValue && (
                                    <MenuItem value={selectedValue}>{replyTargetLabel || 'Recipient'}</MenuItem>
                                )}
                                {receiverType === 'User' ? (
                                    isUsersLoading ? (
                                        <MenuItem disabled value="">
                                            Loading users...
                                        </MenuItem>
                                    ) : (
                                        (normalizedUsers || [])
                                            .filter((item: any) => !currentUserId || Number(item.id) !== currentUserId)
                                            .map((item: any) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {`${item.firstName || ''} ${item.lastName || ''}`.trim()}
                                                </MenuItem>
                                            ))
                                    )
                                ) : (
                                    isBroadcastGroupsLoading ? (
                                        <MenuItem disabled value="">
                                            Loading broadcast groups...
                                        </MenuItem>
                                    ) : (
                                        (broadcastGroups || []).map((group: any) => (
                                            <MenuItem key={group.id} value={group.id}>
                                                {group.groupName || `Group ${group.id}`}
                                            </MenuItem>
                                        ))
                                    )
                                )}
                            </TextField>
                            <TextField
                                name="title"
                                size="small"
                                label="Subject"
                                fullWidth
                                required
                                value={form.title}
                                onChange={handleChange}
                                inputProps={{ maxLength: 250 }}
                                disabled={!!id}
                                error={!!errors.title}
                                helperText={errors.title || undefined}
                            />
                            {!isReply && !id && (
                                <FormControlLabel
                                    sx={{ ml: 0, alignItems: 'center' }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={allowReplyAll}
                                            onChange={(event) => {
                                                setAllowReplyAll(event.target.checked);
                                                setIsFormDirty(true);
                                            }}
                                            sx={{
                                                padding: '4px',
                                                color: 'rgb(68, 99, 136)',
                                                '&.Mui-checked': { color: 'rgb(68, 99, 136)' },
                                            }}
                                        />
                                    }
                                    label="Allow reply to all"
                                />
                            )}
                        </Box>
                    </Grid>

                    {!isReply && (
                    <Grid item xs={12}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1}
                            mb={attachments.length || isUploadingImages ? 1 : 0}
                        >
                            <Typography variant="caption" sx={{ color: '#4b5563' }}>
                                Up to {MAX_IMAGES} images, {MAX_IMAGE_SIZE_MB}MB each.
                            </Typography>
                            {!id && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleOpenImagePicker}
                                    disabled={isUploadingImages || attachments.length >= MAX_IMAGES}
                                    style={{
                                        borderColor: 'rgb(68, 99, 136)',
                                        color: 'rgb(68, 99, 136)',
                                    }}
                                >
                                    Add Image
                                </Button>
                            )}
                        </Box>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            ref={imageInputRef}
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                        {attachments.length > 0 && (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {attachments.map((file, index) => {
                                const imageUrl =
                                    file.previewUrl ||
                                    (file.path
                                        ? `${process.env.REACT_APP_URL || ''}${file.path}`
                                        : null);
                                return (
                                    <Box
                                        key={`${file.id}-${index}`}
                                        sx={{
                                            position: 'relative',
                                            width: 72,
                                            height: 72,
                                            flexShrink: 0,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '1px solid #e0e0e0',
                                            bgcolor: '#f5f5f5',
                                        }}
                                    >
                                        {imageUrl ? (
                                            <Box
                                                component="img"
                                                src={imageUrl}
                                                alt={String(file.name || `Image ${index + 1}`)}
                                                onClick={() =>
                                                    setPreviewImage({
                                                        url: imageUrl,
                                                        alt: String(file.name || `Image ${index + 1}`),
                                                    })
                                                }
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary">
                                                    {file.name || `Image ${index + 1}`}
                                                </Typography>
                                            </Box>
                                        )}
                                        {!id && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveAttachment(index)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)',
                                                    },
                                                    padding: 0.5,
                                                }}
                                                aria-label="Remove image"
                                            >
                                                <Close sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        )}
                        {isUploadingImages && (
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                Uploading images...
                            </Typography>
                        )}
                        {attachmentsError && (
                            <Typography variant="caption" role="alert" sx={{ mt: 0.5, display: 'block', color: '#d32f2f' }}>
                                {attachmentsError}
                            </Typography>
                        )}
                    </Grid>
                    )}

                    <Grid item xs={12}>
                        <Box ref={contentEditorRef} sx={{ scrollMarginTop: '16px' }}>
                            {errors.content && (
                                <Typography
                                    role="alert"
                                    variant="body2"
                                    sx={{
                                        mb: 1.5,
                                        px: 1.5,
                                        py: 1,
                                        display: 'block',
                                        color: '#d32f2f',
                                        backgroundColor: '#fdecea',
                                        border: '1px solid #f5c2c7',
                                        borderRadius: '4px',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    {errors.content}
                                </Typography>
                            )}
                            <Box sx={{
                                '& .quill': {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: 'auto',
                                    backgroundColor: 'white',
                                },
                                '& .ql-toolbar.ql-snow': {
                                    flex: '0 0 auto',
                                    padding: '4px 8px',
                                    borderTop: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                    borderLeft: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                    borderRight: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                    ...(id && {
                                        pointerEvents: 'none',
                                        opacity: 0.6,
                                    }),
                                },
                                '& .ql-container': {
                                    fontSize: '1rem',
                                    fontFamily: 'inherit',
                                    flex: '0 0 auto',
                                    height: 'auto !important',
                                    minHeight: 0,
                                    overflow: 'visible',
                                },
                                '& .ql-container.ql-snow': {
                                    borderBottom: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                    borderLeft: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                    borderRight: errors.content ? '1px solid #d32f2f' : '1px solid #ccc',
                                },
                                '& .ql-editor': {
                                    fontSize: '1rem',
                                    fontFamily: 'inherit',
                                    minHeight: '140px',
                                    height: 'auto',
                                    overflowY: 'visible',
                                },
                            }}>
                                <ReactQuill
                                    key={id || replyQuillKey}
                                    ref={quillRef}
                                    value={value}
                                    onChange={(content) => {
                                        setValue(content);
                                        setIsFormDirty(true);
                                        // Clear error when user adds content (text, images, or links)
                                        const contentText = content.replace(/<[^>]*>/g, '').trim();
                                        const hasImages = /<img[^>]*>/i.test(content);
                                        const hasLinks = /<a[^>]*href[^>]*>/i.test(content);
                                        
                                        if (contentText || hasImages || hasLinks) {
                                            setErrors((prev) => ({ ...prev, content: '' }));
                                        }
                                    }}
                                    modules={modules}
                                    theme="snow"
                                    style={{
                                        backgroundColor: 'white',
                                    }}
                                    placeholder="Enter your message here..."
                                    readOnly={!!id}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </form>

            <Dialog open={openBackDialog} onClose={() => handleDialogClose(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    You have not sent your message. Would you like to send before leaving?
                </DialogContent>
                <DialogActions>
                    <Button
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                        onClick={() => handleDialogClose(false)}
                    >
                        Exit
                    </Button>
                    <Button
                        onClick={() => handleDialogClose(true)}
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openUrlDialog} onClose={() => setOpenUrlDialog(false)}>
                <DialogTitle>Add URL</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '400px', pt: 1 }}>
                        <TextField
                            label="URL"
                            fullWidth
                            required
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder="https://example.com"
                        />
                        <TextField
                            label="Display Text"
                            fullWidth
                            value={urlDisplayText}
                            onChange={(e) => setUrlDisplayText(e.target.value)}
                            placeholder="Link text (optional)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenUrlDialog(false)}
                        style={{
                            color: 'rgb(68, 99, 136)',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUrlDialogSubmit}
                        disabled={!urlInput.trim()}
                        style={{
                            backgroundColor: 'rgb(68, 99, 136)',
                            color: 'white',
                            padding: '5px 12px',
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Image preview modal */}
            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    <Typography variant="subtitle1" noWrap sx={{ mr: 2 }}>
                        {previewImage?.alt}
                    </Typography>
                    <IconButton onClick={() => setPreviewImage(null)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewImage && (
                        <Box
                            component="img"
                            src={previewImage.url}
                            alt={previewImage.alt}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default AddBroadcast;
